/**
 * 수료증 발급·인쇄 — 발급 목록, 교회 수령 확인, 선택 인쇄/PDF, 날짜 변경, 취소
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, InputAdornment, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { api, CertData } from '../api';
import { C, CLUBS, certDate, downloadBlob, mdhm, toCsv, ymd } from '../common';
import { CertDateDialog, CertRevokeDialog } from './certDialogs';
import { Empty, Kpi, ToneChip } from './ui';

type Row = CertData & { year: number; adminPrints: number; revokeReason?: string; dateChanges: any[] };

const CertificatesTab: React.FC<{ initialReceipt?: string }> = ({ initialReceipt }) => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [club, setClub] = useState('');
  const [receipt, setReceipt] = useState(initialReceipt || '');
  const [status, setStatus] = useState('issued');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [summary, setSummary] = useState({ issued: 0, received: 0, notReceived: 0 });
  const [checked, setChecked] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [dateCert, setDateCert] = useState<Row | null>(null);
  const [revokeCert, setRevokeCert] = useState<Row | null>(null);

  const load = () => {
    const p = new URLSearchParams({ status, q });
    if (year) p.set('year', year);
    if (club) p.set('club', club);
    if (receipt) p.set('receipt', receipt);
    api<{ data: Row[]; summary: typeof summary }>(`/admin/cert/certificates?${p}`)
      .then((r) => { setRows(r.data); setSummary(r.summary); setChecked([]); })
      .catch((e) => setError(e.message));
  };
  useEffect(() => { const t = window.setTimeout(load, 250); return () => window.clearTimeout(t); }, [year, club, receipt, status, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const printSelected = () => window.open(`/admin/bt-cert/print?ids=${(checked.length ? checked : rows.filter((r) => r.status === 'issued').map((r) => r._id)).join(',')}`, '_blank');
  const exportCsv = () => {
    const out: Array<Array<string | number>> = [['일련번호', '이름', '영문 이름', '교회', '등록번호', '클럽', '수료일', '발급일', '교회 PDF', '교회 인쇄', '최근 수령', '수령 담당자', '관리자 인쇄', '상태']];
    rows.forEach((r) => out.push([r.serial, r.teacherName, r.teacherNameEn, r.churchName, r.regNo || '', r.clubLabel, ymd(r.completedAt), ymd(r.issuedAt),
      r.churchReceipt?.pdf || 0, r.churchReceipt?.print || 0, r.churchReceipt?.lastAt ? mdhm(r.churchReceipt.lastAt) : '', r.churchReceipt?.lastWho || '', r.adminPrints, r.status === 'issued' ? '발급' : '취소']));
    downloadBlob(new Blob([toCsv(out)], { type: 'text/csv;charset=utf-8' }), `BT수료증_${year || '전체'}.csv`);
  };

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2, maxWidth: 720 }}>
        <Kpi label={`발급 ${year ? `(${year})` : ''}`} value={summary.issued} tone="ok" onClick={() => setReceipt('')} />
        <Kpi label="교회 수령" value={summary.received} sub="PDF 다운로드 또는 인쇄" onClick={() => setReceipt('received')} />
        <Kpi label="미수령" value={summary.notReceived} tone={summary.notReceived ? 'warn' : 'plain'} onClick={() => setReceipt('none')} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
        <TextField select size="small" label="발행연도" value={year} onChange={(e) => setYear(e.target.value)} sx={{ width: 110 }}>
          <MenuItem value="">전체</MenuItem>{[thisYear, thisYear - 1, thisYear - 2].map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="클럽" value={club} onChange={(e) => setClub(e.target.value)} sx={{ width: 140 }}>
          <MenuItem value="">전체</MenuItem>{CLUBS.map((c) => <MenuItem key={c.code} value={c.code}>{String(c.no).padStart(2, '0')} {c.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="교회 수령" value={receipt} onChange={(e) => setReceipt(e.target.value)} sx={{ width: 120 }}>
          <MenuItem value="">전체</MenuItem><MenuItem value="received">수령</MenuItem><MenuItem value="none">미수령</MenuItem>
        </TextField>
        <TextField select size="small" label="상태" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 110 }}>
          <MenuItem value="issued">발급</MenuItem><MenuItem value="revoked">취소됨</MenuItem><MenuItem value="all">전체</MenuItem>
        </TextField>
        <TextField size="small" placeholder="일련번호 · 이름 · 교회" value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="outlined" onClick={exportCsv}>CSV</Button>
        <Button variant="contained" disabled={!rows.length} onClick={printSelected}>🖨 {checked.length ? `선택 ${checked.length}장` : '목록 전체'} 인쇄 / PDF</Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {!rows.length ? <Empty>수료증이 없습니다.</Empty> : (
        <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead><TableRow sx={{ '& th': { fontSize: 12.5, color: C.sub, fontWeight: 700, bgcolor: '#fffbea', whiteSpace: 'nowrap' } }}>
              <TableCell padding="checkbox"><Checkbox size="small" checked={checked.length === rows.length} indeterminate={checked.length > 0 && checked.length < rows.length}
                onChange={(e) => setChecked(e.target.checked ? rows.map((r) => r._id) : [])} /></TableCell>
              <TableCell>일련번호</TableCell><TableCell>이름</TableCell><TableCell>교회</TableCell><TableCell>클럽</TableCell>
              <TableCell>수료일</TableCell><TableCell>교회 수령</TableCell><TableCell>관리자 인쇄</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {rows.map((r) => {
                const rc = r.churchReceipt;
                const got = rc && rc.pdf + rc.print > 0;
                return (
                  <TableRow key={r._id} hover sx={{ opacity: r.status === 'revoked' ? 0.5 : 1 }}>
                    <TableCell padding="checkbox"><Checkbox size="small" checked={checked.includes(r._id)} onChange={(e) => setChecked(e.target.checked ? [...checked, r._id] : checked.filter((x) => x !== r._id))} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.serial}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography fontSize={14} fontWeight={700}>{r.teacherName}</Typography><Typography fontSize={11.5} color={C.sub}>{r.teacherNameEn}</Typography></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{r.churchName}<Typography fontSize={11.5} color={C.sub}>{r.regNo}</Typography></TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{r.clubLabel}</TableCell>
                    <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{certDate(r.completedAt)}{r.dateChanges?.length ? <Typography fontSize={11} color={C.warn}>날짜 변경됨</Typography> : null}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.status === 'revoked' ? <ToneChip tone="err" label={`취소 · ${r.revokeReason || ''}`} />
                        : got ? (
                          <Box>
                            <ToneChip tone="ok" label={`수령 · PDF ${rc!.pdf} · 인쇄 ${rc!.print}`} />
                            <Typography fontSize={11} color={C.sub}>{mdhm(rc!.lastAt)} · {rc!.lastWho}</Typography>
                            {r.needsReReceive && <Typography fontSize={11} color={C.warn}>날짜 변경 후 미수령</Typography>}
                          </Box>
                        ) : <ToneChip tone="gray" dashed label="미수령" />}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{r.adminPrints ? `${r.adminPrints}회` : '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.status === 'issued' && <>
                        <Button size="small" onClick={() => window.open(`/admin/bt-cert/print?ids=${r._id}`, '_blank')}>인쇄</Button>
                        <Button size="small" onClick={() => setDateCert(r)}>날짜</Button>
                        <Button size="small" color="error" onClick={() => setRevokeCert(r)}>취소</Button>
                      </>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography fontSize={12} color={C.sub} mt={1}>일련번호 = 발행연도-클럽번호-발급순서 (01 PUGGLES · 02 CUBBIES · 03 SPARKS · 04 T&T · 05 TREK · 06 JOURNEY). 취소된 번호는 다시 쓰지 않습니다.</Typography>
      <CertDateDialog cert={dateCert} onClose={() => setDateCert(null)} onDone={load} />
      <CertRevokeDialog cert={revokeCert} onClose={() => setRevokeCert(null)} onDone={load} />
    </Box>
  );
};

export default CertificatesTab;
